import { GameConfig } from "@dragonsight/models";
import { Api } from "../../shared/api";
import { useParams } from "react-router-dom";
import { Checkbox, MenuItem, Select, Stack, styled, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Formik } from "formik";
import { TextInput } from "../../components/form";
import ContentContainer from "../../components/contentContainer";
import Section from "../../components/section";
import Button from "../../components/button";
import { TextInputControlled } from "../../components/form/textInput";

import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import IconButton from "../../components/iconButton";
import { AddAlert } from "../../shared/alert";
import { useConfirmModal } from "../../components/confirmModal";
import CopyBox from "../../components/copybox";
import { token } from "../../shared/theme";

const ConfigRow = styled('div')({
    height: "50px",
    display: "flex",
    alignItems: "center",
})

const ConfigName = styled('div')({
    fontWeight: 700,
    width: "30%",
    flexShrink: 0
});

const ConfigType = styled('div')({
    width: "30%",
    flexShrink: 0
})

const ConfigValue = styled('div')({
    flexGrow: 1
})

const ConfigChanged = styled('div')({
    marginLeft: "10px",
    flexShrink: 0
})

const publicApi = process.env.REACT_APP_PUBLIC_API_ENDPOINT;

type FormGameConfig = Omit<GameConfig, "id" | "gameId" | "configValue">

const GameConfigRow = (props: { config: GameConfig, onChange: (value: string | number | boolean) => void, onDelete: () => void}) => {
    const { config, onChange, onDelete } = props;
    const [ value, setValue ] = useState(config.configValue);
    const [ changed, setChanged ] = useState(false);
    const confirm = useConfirmModal();

    const valueChanged = (value: string | number | boolean) => {
        setChanged(true);
        setValue(value);
    }

    const resetChanged = () => {
        setValue(config.configValue);
        setChanged(false);
    }

    const submit = () => {
        setChanged(false);
        onChange(value);
    }

    const handleDelete = async () => {
        const willDelete = await confirm.confirm({
            message: "Are you sure you want to delete this game config.",
            title: `Delete ${config.configName}`
        });

        if(willDelete) {
            onDelete();
        }
    }

    return <ConfigRow key={config.id}>
        <ConfigName>{config.configName}</ConfigName>
        <ConfigType>{config.configType}</ConfigType>
        <ConfigValue>
        {config.configType === "string" && <TextInputControlled size="small" placeholder="value" value={value as string} onChange={(e) => valueChanged(e.target.value)} />}
        {config.configType === "number" && <TextInputControlled size="small" placeholder="value" type="number" value={value as number} onChange={(e) => valueChanged(e.target.value)} />}
        {config.configType === "boolean" && <Select size="small" onChange={(e) => {
            valueChanged(e.target.value === "true" ? true : false);
        }} value={value === true ? 'true' : 'false'}>
                    <MenuItem value={'true'}>True</MenuItem>
                    <MenuItem value={'false'}>False</MenuItem>
                </Select>}
        </ConfigValue>
        <ConfigChanged>
            { changed ? <>
                <IconButton onClick={() => submit()}><SaveIcon  /> </IconButton>  
                <IconButton onClick={() => resetChanged()}><RestartAltIcon  /> </IconButton>  
            </> : <>
                <IconButton onClick={() => handleDelete()}><DeleteIcon/> </IconButton>  
            </>}
        </ConfigChanged>
    </ConfigRow>
}

const NewItem = (props: { onSubmit: (values: FormGameConfig) => void}) => {
    return <Formik<FormGameConfig> onSubmit={props.onSubmit} initialValues={{configName: "", configType: "boolean"}}>
        {({values, handleChange, submitForm}) => (
            <>
                <Stack spacing={2}>
                    <TextInput name="configName" placeholder="Name" />
                    <Select name="configType" onChange={handleChange} value={values.configType}>
                        <MenuItem value={"string"}>String</MenuItem>
                        <MenuItem value={"boolean"}>Bool</MenuItem>
                        <MenuItem value={"number"}>Number</MenuItem>
                    </Select>
                    <Stack direction={"row"}>
                        <Button variant="contained" onClick={() => submitForm()}>Add config</Button>
                    </Stack>
                </Stack>
            </>
        )}
    </Formik>
}


const GameConfigScreen = () => {
    const { gameId } = useParams();
    const [ configs, setConfigs ] = useState<GameConfig[]>([]);
    const [ loading, setLoading ] = useState(false);

    const list = async (search: "", nextToken?: string) => {
        setLoading(true);
        try {
            const response = await Api.Game.Config.List(gameId!, undefined, undefined, undefined);
            setConfigs(response.items);
        } finally {
            setLoading(false);
        }

    }

    const addConfig = async (values: FormGameConfig) => {
        const response = await Api.Game.Config.Add(gameId!, values.configName, values.configType);

        setConfigs((current) => {
            return [
                ...current,
                response
            ]
        });
    }

    const editConfig = async (id: string, value: string | number | boolean) => {
        try {
            await Api.Game.Config.Update(id, gameId!, value)
            AddAlert({
                message: "Config updated",
                severity: "success"
            });

        } catch (e: any) {
            AddAlert({
                message: "Failed to update config.",
                severity: "error"
            });
        }
    }

    const deleteConfig = async (id: string) => {
        try {
            await Api.Game.Config.Delete(id, gameId!)
            AddAlert({
                message: "Config Removed",
                severity: "success"
            });
            setConfigs((current) => {
                return [...current.filter(c => c.id != id)]
            })

        } catch (e: any) {
            AddAlert({
                message: "Failed to remove config.",
                severity: "error"
            });
        }
    }

    
    useEffect(() => {
        list("", undefined);
    }, [])
    
    if(loading) return <>Loading</>
    
    const url = `${publicApi}getgameconfig/${gameId}`;

    return <>
        <ContentContainer>
            <Typography fontSize={token.font.size.small}>
                Config URL :
                <CopyBox content={url} />
            </Typography>
            {configs.length === 0 ? <>There are no configs to show</> : <>
                {configs.map((c) => {
                    return <GameConfigRow config={c} onChange={(e) => editConfig(c.id, e)} onDelete={() => deleteConfig(c.id)} key={c.id} />
                })}
            </>}
        </ContentContainer>

        <ContentContainer>
            <Section header="New Config">
                <NewItem onSubmit={addConfig} />
            </Section>
        </ContentContainer>
    </>
}

export default GameConfigScreen;